"use client";

import { useEffect, useState, useRef } from "react";

interface AnimatedStatProps {
    value: string;
    suffix?: string; // Dışarıdan gelen Ek (+)
    animation?: string;
    className?: string;
    style?: React.CSSProperties;
    suffixStyle?: React.CSSProperties; // Ek kısmına uygulanacak özel renk/stil
}

export function AnimatedStat({ value, suffix = "", animation = "none", className = "", style, suffixStyle }: AnimatedStatProps) {
    const [count, setCount] = useState(0);
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    // Kullanıcının "value" içine yazdığı olası gizli ekleri ve sayıyı ayır
    const numMatch = value?.toString().match(/^([^0-9]*)([0-9.,]+)([^0-9]*)$/);

    let isNumber = false;
    let targetNumber = 0;
    let internalPrefix = "";
    let internalSuffix = "";

    const shouldCount = animation === "counter" || animation === "counter-zoom";

    if (numMatch) {
        isNumber = true;
        internalPrefix = numMatch[1];
        targetNumber = parseInt(numMatch[2].replace(/[.,]/g, ""));
        internalSuffix = numMatch[3];
    }

    // Hem value içinden çıkan gizli suffix'i hem de prop olarak gelen suffix'i birleştir
    const finalSuffix = `${internalSuffix}${suffix}`;

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );

        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        // Sadece sayıysa ve sayma animasyonu seçildiyse say
        if (!isVisible || !isNumber || !shouldCount) {
            if (isNumber) setCount(targetNumber); // Animasyon yoksa direkt son sayıyı bas
            return;
        }

        let start = 0;
        const duration = 2000;
        const increment = targetNumber / (duration / 16);

        const timer = setInterval(() => {
            start += increment;
            if (start >= targetNumber) {
                setCount(targetNumber);
                clearInterval(timer);
            } else {
                setCount(Math.ceil(start));
            }
        }, 16);

        return () => clearInterval(timer);
    }, [isVisible, isNumber, targetNumber, shouldCount]);

    const formattedCount = count.toLocaleString('tr-TR');

    let animationClass = className;
    if (animation === "pulse") animationClass += " animate-pulse";
    if (animation === "bounce") animationClass += " animate-bounce";
    if (animation === "fade-up") {
        animationClass += ` transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`;
    }
    if (animation === "zoom-in" || animation === "counter-zoom") {
        animationClass += ` transition-all duration-1000 ease-out ${isVisible ? "scale-100 opacity-100" : "scale-50 opacity-0"}`;
    }

    return (
        <div ref={ref} className={animationClass} style={style}>
            {isNumber ? (
                <>
                    {internalPrefix}
                    {formattedCount}
                    {/* Ek kısmını burada özel stil (renk) ile sarmalıyoruz */}
                    {finalSuffix && <span style={suffixStyle}>{finalSuffix}</span>}
                </>
            ) : (
                <>
                    {value}
                    {finalSuffix && <span style={suffixStyle}>{finalSuffix}</span>}
                </>
            )}
        </div>
    );
}